import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import type { Shares, Slice } from './category-shares'

/**
 * A rosca de gastos por categoria, em SVG puro — agora com **dois anéis**.
 *
 * Sem biblioteca de gráfico: são arcos de um anel, e a matemática cabe em trinta
 * linhas. Uma dependência de gráfico traria um runtime inteiro para desenhar
 * `stroke-dasharray` num círculo — e `Docs/04-Design/Liquid Glass.md` já pesa o
 * orçamento de renderização desta tela.
 *
 * ## O anel de dentro é o pai; o de fora, as filhas
 *
 * O de fora aparece **só para a categoria em destaque** — o ponteiro por cima da
 * linha da legenda, ou o toque nela — e, com o botão "Sempre mostrar
 * subcategorias" ligado, para todas de uma vez. É a forma que o dono pediu.
 * Cada arco de fora mora dentro do arco do seu pai: as filhas somam o pai, e ver
 * isso é metade do que o gráfico tem a dizer.
 *
 * ## O truque do `stroke-dasharray`
 *
 * Cada fatia é o **mesmo círculo** com um traço grosso, um `dasharray` do
 * tamanho do arco e um `dashoffset` que o gira até a posição. É o jeito que não
 * precisa de trigonometria nem de `path` com `A` — e que degrada bem: sem CSS,
 * sobra um círculo.
 *
 * ## Duas fatias vizinhas não se encostam
 *
 * Cada arco perde 2px do próprio comprimento, e no vão aparece o fundo. Sem
 * isso, duas fatias de cores próximas viram uma fatia só — e é o mesmo motivo
 * pelo qual ícone colado em ícone lê como um controle só (decisão 292).
 *
 * ## O gráfico é `aria-hidden`, e a legenda é o conteúdo
 *
 * Um SVG de arcos não tem o que dizer a um leitor de tela. A informação inteira
 * — nome, valor e percentual de cada fatia — está na lista ao lado, que é texto
 * de verdade. Descrever a rosca com `<title>` duplicaria isso e leria pior.
 *
 * **E é por isso que quem comanda o destaque é a LEGENDA, e não o arco.** Um
 * arco de 3% num anel de 176px é um alvo de toque de poucos pixels, e um
 * `<circle>` clicável não chega ao teclado nem ao leitor de tela sem virar um
 * botão de mentira. A linha da legenda é um `<button>` de verdade, larga, com o
 * nome dentro — e o mouse por cima dela acende o anel de fora antes do clique.
 */

/**
 * As cores das fatias, na ordem. **Não vêm de `Category.color`** — cada
 * categoria tem a sua, e uma rosca com as cores que sete pessoas escolheram
 * separadamente não tem contraste entre fatias vizinhas, que é a única coisa
 * que a rosca precisa ter. A cor aqui é posicional, e a legenda é quem liga cor
 * a nome.
 *
 * **A ordem foi medida, e não escolhida a olho** (decisão 293): o validador de
 * paleta reprovava a lista anterior por luminosidade fora da faixa no fundo
 * escuro, e reprovava duas ordens seguintes por separação insuficiente para
 * quem não distingue verde e vermelho — verde vizinho de vermelho, verde
 * vizinho de âmbar. Esta passa nos cinco testes contra a superfície `#171c24`.
 *
 * "Outros" e "Sem categoria" recebem cinza de propósito: as duas são ausência de
 * informação, e uma cor viva as faria parecer uma categoria como as outras.
 */
const CORES = ['#059669', '#7c3aed', '#d97706', '#0284c7', '#e11d48', '#0d9488', '#8b5cf6']
const CINZA = '#94a3b8'

function colorOf(slice: Slice, index: number): string {
  if (slice.id === null || slice.id === 'outros') return CINZA
  return CORES[index % CORES.length] ?? CINZA
}

/**
 * A cor de uma filha: a do pai, clareada por degraus.
 *
 * Identidade pela MATIZ (de quem é este gasto) e ordem pela luminosidade (qual
 * filha é maior) — é o que separa um anel externo legível de vinte cores novas
 * competindo entre si. Mistura em `oklab` para os degraus serem perceptualmente
 * iguais; `color-mix` é CSS, e o `stroke` de um SVG aceita.
 */
function tomDaFilha(corDoPai: string, indice: number, total: number): string {
  if (total <= 1) return corDoPai
  const clareia = Math.round((indice / (total - 1)) * 45)
  return `color-mix(in oklab, ${corDoPai} ${String(100 - clareia)}%, white)`
}

const RAIO_INTERNO = 62
const ESPESSURA_INTERNA = 26
const RAIO_EXTERNO = 84
const ESPESSURA_EXTERNA = 12
/** O vão entre duas fatias, em pixels do `viewBox`. Ver o cabeçalho. */
const VAO = 2

type Arco = { length: number; offset: number }

/**
 * O comprimento e o deslocamento de cada arco, na ordem das fatias.
 *
 * O `offset` é o **negativo** do que já foi desenhado: `stroke-dashoffset`
 * desloca o traço para trás, então acumular positivo giraria a rosca no sentido
 * errado e as fatias se sobreporiam.
 *
 * `inicio` e `fracao` existem para o anel de fora: as filhas de um pai ocupam
 * **o arco daquele pai**, e não a volta inteira.
 */
function arcsOf(
  slices: readonly Pick<Slice, 'cents'>[],
  raio: number,
  inicio = 0,
  fracao = 1,
): Arco[] {
  const circunferencia = 2 * Math.PI * raio
  const total = slices.reduce((soma, slice) => soma + Math.abs(slice.cents), 0)
  if (total === 0) return slices.map(() => ({ length: 0, offset: 0 }))

  const arcos: Arco[] = []
  let acumulado = inicio * circunferencia
  for (const slice of slices) {
    const cheio = (Math.abs(slice.cents) / total) * fracao * circunferencia
    // Nunca negativo: uma fatia menor que o vão vira um risco, e não some.
    arcos.push({ length: Math.max(cheio - VAO, 0.5), offset: -acumulado })
    acumulado += cheio
  }
  return arcos
}

export function Donut({
  shares,
  title,
  subs,
  destacada,
  onDestacar,
  sempreSubs = false,
}: {
  shares: Shares
  title: string
  /** As filhas de cada pai, por id. Ausente = rosca de um anel só. */
  subs?: Record<string, Slice[]>
  /** O pai cujas filhas estão à mostra agora. */
  destacada?: string | null
  /** Ausente = rosca só de leitura, sem destaque nenhum. */
  onDestacar?: (categoryId: string | null) => void
  /** Todas as filhas à mostra, e não só as do destaque. */
  sempreSubs?: boolean
}) {
  if (shares.slices.length === 0) {
    return (
      <p className="text-sm text-[var(--text-muted)]">
        Nenhum gasto categorizável neste mês.
      </p>
    )
  }

  // **Os arcos saem de uma função pura, não de um acumulador dentro do JSX.**
  // A versão com `let acumulado` mutado no `.map` é reprovada por
  // `react-hooks/immutability`, e com razão: uma variável de módulo mutada
  // durante a renderização acumula errado se o React reexecutar o corpo.
  const arcos = arcsOf(shares.slices, RAIO_INTERNO)
  const total = shares.slices.reduce((soma, slice) => soma + Math.abs(slice.cents), 0)

  const mostraSubs = (slice: Slice): Slice[] =>
    slice.id !== null && (sempreSubs || destacada === slice.id) ? (subs?.[slice.id] ?? []) : []

  // Onde começa o arco de cada pai, em voltas: é daí que o anel de fora parte.
  const inicios: number[] = []
  let andado = 0
  for (const slice of shares.slices) {
    inicios.push(andado)
    andado += total === 0 ? 0 : Math.abs(slice.cents) / total
  }

  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg viewBox="0 0 200 200" className="h-44 w-44 shrink-0" aria-hidden="true">
        <g transform="rotate(-90 100 100)">
          {shares.slices.map((slice, index) => {
            const arco = arcos[index] ?? { length: 0, offset: 0 }
            const filhas = mostraSubs(slice)
            const fracao = total === 0 ? 0 : Math.abs(slice.cents) / total
            const arcosDasFilhas = arcsOf(filhas, RAIO_EXTERNO, inicios[index] ?? 0, fracao)
            const cor = colorOf(slice, index)
            return (
              <g key={slice.id ?? 'sem-categoria'}>
                <circle
                  cx="100"
                  cy="100"
                  r={RAIO_INTERNO}
                  fill="none"
                  stroke={cor}
                  strokeWidth={ESPESSURA_INTERNA}
                  strokeDasharray={`${String(arco.length)} ${String(2 * Math.PI * RAIO_INTERNO - arco.length)}`}
                  strokeDashoffset={String(arco.offset)}
                  // Some quando OUTRA está em destaque: com uma fatia aberta, o
                  // resto da rosca é contexto, e não competição.
                  opacity={destacada === null || destacada === undefined || destacada === slice.id ? 1 : 0.45}
                />
                {filhas.map((filha, i) => {
                  const externo = arcosDasFilhas[i] ?? { length: 0, offset: 0 }
                  return (
                    <circle
                      key={filha.id ?? `direto-${String(i)}`}
                      cx="100"
                      cy="100"
                      r={RAIO_EXTERNO}
                      fill="none"
                      stroke={tomDaFilha(cor, i, filhas.length)}
                      strokeWidth={ESPESSURA_EXTERNA}
                      strokeDasharray={`${String(externo.length)} ${String(2 * Math.PI * RAIO_EXTERNO - externo.length)}`}
                      strokeDashoffset={String(externo.offset)}
                    />
                  )
                })}
              </g>
            )
          })}
        </g>
        {/* O total no centro, como a nota pede. Fora do `<g>` girado: ele não
            deve herdar a rotação dos arcos. */}
        <text
          x="100"
          y="106"
          textAnchor="middle"
          className="fill-[var(--text)] text-[18px] font-semibold"
        >
          {formatBRL(absCents(shares.totalCents))}
        </text>
      </svg>

      <ul aria-label={title} className="min-w-48 flex-1 text-sm">
        {shares.slices.map((slice, index) => {
          const cor = colorOf(slice, index)
          const filhas = mostraSubs(slice)
          // "Outros" e "Sem categoria" nunca abrem: a primeira não é uma
          // categoria, e a segunda não tem filhas por definição.
          const abre =
            onDestacar !== undefined &&
            slice.id !== null &&
            slice.id !== 'outros' &&
            (subs?.[slice.id]?.length ?? 0) > 0
          const conteudo = (
            <>
              <span
                aria-hidden="true"
                className="inline-block size-3 shrink-0 rounded-full"
                style={{ backgroundColor: cor }}
              />
              <span className="flex-1 text-left">{slice.name}</span>
              <span className="tabular-nums text-[var(--text-muted)]">
                {`${slice.percent.toFixed(1).replace('.', ',')}%`}
              </span>
              <span className="tabular-nums">{formatBRL(absCents(slice.cents))}</span>
            </>
          )
          return (
            <li
              key={slice.id ?? 'sem-categoria'}
              className="border-b border-[var(--border)] py-1"
            >
              {abre ? (
                <button
                  type="button"
                  className="flex w-full items-baseline gap-2"
                  aria-expanded={filhas.length > 0}
                  // O ponteiro acende, o toque fixa: no celular não há `hover`,
                  // e o clique é o único jeito de pedir as filhas.
                  onMouseEnter={() => {
                    if (!sempreSubs && slice.id !== null) onDestacar(slice.id)
                  }}
                  onMouseLeave={() => {
                    if (!sempreSubs) onDestacar(null)
                  }}
                  onFocus={() => {
                    if (!sempreSubs && slice.id !== null) onDestacar(slice.id)
                  }}
                  onClick={() => {
                    if (slice.id !== null) onDestacar(destacada === slice.id ? null : slice.id)
                  }}
                >
                  {conteudo}
                </button>
              ) : (
                <span className="flex items-baseline gap-2">{conteudo}</span>
              )}

              {filhas.length > 0 && (
                <ul aria-label={`Subcategorias de ${slice.name}`} className="mb-1 ml-5">
                  {filhas.map((filha, i) => (
                    <li
                      key={filha.id ?? `direto-${String(i)}`}
                      className="flex items-baseline gap-2 text-xs text-[var(--text-muted)]"
                    >
                      <span
                        aria-hidden="true"
                        className="inline-block size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: tomDaFilha(cor, i, filhas.length) }}
                      />
                      <span className="flex-1 text-left">{filha.name}</span>
                      <span className="tabular-nums">{formatBRL(absCents(filha.cents))}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
